/**
 * WORK INBOX V2 — job flags.
 *
 * A job with open tasks gets:
 *   - a full-width colored FLAG STRIP inside the job header (JobTaskFlagStrip)
 *   - a corner tab (⚑ N tasks) on Grid / Close Out / list cards
 *     (JobTaskCornerTab)
 *
 * Color follows the hottest task's heat. Data comes from the batch endpoint
 * GET /api/tasks/job-flags?jobIds=… — one request per surface regardless of
 * how many cards are on screen.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { afterLunchIso } from "@/lib/tasks";
import { openTaskFlyout } from "@/lib/task-flyout";
import { Button } from "@/components/ui/button";

export interface JobTaskFlag {
    count: number;
    hottest: { id: string; title: string; heat: "red" | "amber" | "none"; owner: string | null; effortMinutes: number };
}

export type JobTaskFlagMap = Record<string, JobTaskFlag>;

/** Batch flags for a set of job ids (skips the request when empty). */
export function useJobTaskFlags(jobIds: string[]): JobTaskFlagMap {
    const key = useMemo(() => [...jobIds].sort().join(","), [jobIds]);
    const q = useQuery({
        queryKey: ["job-task-flags", key],
        queryFn: () => api.get<{ flags: JobTaskFlagMap }>(`/api/tasks/job-flags?jobIds=${key}`),
        enabled: key.length > 0,
        staleTime: 30_000,
    });
    return q.data?.flags ?? {};
}

// Board surfaces (Grid, Close Out queue) render dozens of cards: the parent
// provides one batched fetch via context; each card reads its own flag.
const FlagContext = createContext<JobTaskFlagMap>({});

export function JobTaskFlagProvider({ jobIds, children }: { jobIds: string[]; children: ReactNode }) {
    const flags = useJobTaskFlags(jobIds);
    return <FlagContext.Provider value={flags}>{children}</FlagContext.Provider>;
}

export function useJobTaskFlag(jobId: string): JobTaskFlag | undefined {
    return useContext(FlagContext)[jobId];
}

const STRIP_TONE: Record<string, string> = {
    red: "border-red-500/40 bg-red-500/10 text-red-800",
    amber: "border-amber-500/40 bg-amber-500/10 text-amber-800",
    none: "border-border bg-muted/50 text-foreground/80",
};

const TAB_TONE: Record<string, string> = {
    red: "bg-red-600 text-white",
    amber: "bg-amber-700 text-white",
    none: "bg-muted-foreground/80 text-white",
};

/**
 * Full-width flag strip INSIDE the job header (not a peer card):
 * "N tasks holding this job — '<hottest title>'" + owner + time guess +
 * [Do it now] [Later today]. "Do it now" opens the task drawer over the job —
 * you never leave the job page (Corbin, 10/16).
 */
export function JobTaskFlagStrip({ jobId }: { jobId: string }) {
    const flags = useJobTaskFlags([jobId]);
    const flag = flags[jobId];
    const qc = useQueryClient();

    const snoozeMut = useMutation({
        mutationFn: (taskId: string) => api.post(`/api/tasks/${taskId}/snooze`, { until: afterLunchIso() }),
        // Returning the refetch keeps the button disabled until the strip
        // actually reflects the snooze, so there is no window where a stale
        // count is clickable.
        onSuccess: async () => {
            toast.success("Snoozed until after lunch — it'll come back");
            await Promise.all([
                qc.invalidateQueries({ queryKey: ["job-task-flags"] }),
                qc.invalidateQueries({ queryKey: ["task-triage"] }),
            ]);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
    });

    // The strip follows the server, not a local "I pressed it" flag. It used to
    // hide itself outright on snooze, which lied twice: the task came back on the
    // next page load (the endpoint ignored `snoozed_until` — #1365), and
    // snoozing ONE task hid a strip that was reporting three. Now the refetch
    // decides, so the count stays true and the strip only goes when the job is
    // genuinely clear.
    if (!flag) return null;
    const { hottest } = flag;

    return (
        <div
            className={cn(
                "flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-sm",
                STRIP_TONE[hottest.heat],
            )}
            data-testid="job-flag-strip"
            data-heat={hottest.heat}
        >
            <Flag className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1 truncate font-medium">
                {flag.count} task{flag.count === 1 ? "" : "s"} holding this job — “{hottest.title}”
            </span>
            <span className="hidden text-xs opacity-80 sm:inline">
                {hottest.owner ?? "unassigned"} · ~{hottest.effortMinutes} min
            </span>
            <div className="flex shrink-0 items-center gap-1.5">
                <Button size="sm" className="h-7 px-2.5 text-xs" onClick={() => openTaskFlyout(hottest.id)}>
                    Do it now
                </Button>
                <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2.5 text-xs bg-transparent"
                    disabled={snoozeMut.isPending}
                    onClick={() => snoozeMut.mutate(hottest.id)}
                >
                    Later today
                </Button>
            </div>
        </div>
    );
}

/**
 * Corner tab for Grid / Close Out / list cards: ⚑ N on the card's top edge.
 * Parent must be `relative`. Renders nothing when the job has no open tasks.
 */
export function JobTaskCornerTab({ flag, className }: { flag: JobTaskFlag | undefined; className?: string }) {
    if (!flag) return null;
    return (
        <span
            className={cn(
                "pointer-events-none absolute -top-1.5 right-1.5 z-10 inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[10px] font-semibold shadow-sm",
                TAB_TONE[flag.hottest.heat],
                className,
            )}
            title={`${flag.count} open task${flag.count === 1 ? "" : "s"} — ${flag.hottest.title}`}
            data-testid="job-flag-tab"
            data-heat={flag.hottest.heat}
        >
            <Flag className="h-2.5 w-2.5" />
            {flag.count}
        </span>
    );
}
